import { MouseEvent, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Tag } from '@givebutter/primitives/tag';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchReportSchedules } from 'api/reports';
import Content from 'assets/js/components/Content';
import * as DeleteRecurringEmailDialog from 'assets/js/pages/reports/DeleteRecurringEmailDialog';
import * as PauseReportScheduleDialog from 'assets/js/pages/reports/PauseReportScheduleDialog';
import * as ResumeReportScheduleDialog from 'assets/js/pages/reports/ResumeReportScheduleDialog';
import * as Breadcrumbs from 'components/breadcrumbs';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip/Tooltip';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ReportSchedule, ReportScheduleRecurringFrequency } from 'types/domain';

const columnHelper = createColumnHelper<ReportSchedule>();
const EMAIL_SLICE = 3;

const ReportSchedulesIndex = () => {
    const { accountId } = useCurrentAccount();
    const { data: report } = useCurrentReport();
    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportSchedulesWithFilters(accountId, report.id, params),
        queryFn: () => fetchReportSchedules(accountId, report.id, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('title', {
                header: 'Email name',
                cell: () => <Table.CellDataOrEmpty />,
            }),
            columnHelper.accessor('recipients', {
                header: 'Recipients',
                cell: info => {
                    const emails = info.getValue() ?? [];
                    const inlineEmails = emails.slice(0, Math.min(emails.length, EMAIL_SLICE));
                    const hoverEmails = emails.slice(Math.min(emails.length, EMAIL_SLICE));

                    if (inlineEmails.length === 0) {
                        return <EmptyData />;
                    }

                    return (
                        <div className={'flex gap-1.5 py-2'}>
                            <div className={'flex gap-1.5'}>
                                {inlineEmails.map(email => (
                                    <Tag key={email} size="sm">
                                        {email}
                                    </Tag>
                                ))}
                            </div>
                            {hoverEmails.length > 0 && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger type={'button'}>
                                        <Tag size="sm">+ {hoverEmails.length} more</Tag>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content
                                            onClick={(e: MouseEvent) => e.preventDefault()}
                                            className={'flex !max-w-[250px] flex-wrap gap-1.5'}
                                        >
                                            {hoverEmails.map(email => (
                                                <Tag key={email} size="sm">
                                                    {email}
                                                </Tag>
                                            ))}
                                            <Tooltip.Arrow />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                    );
                },
                meta: {
                    className: '[&>*]:h-auto !h-auto',
                },
            }),
            columnHelper.accessor('last_sent_at', {
                header: () => (
                    <div className={'flex gap-1'}>
                        <p>Last sent</p>
                        <p className={'text-xs leading-5 text-gray-400'}>
                            ({DateTime.now().offsetNameShort})
                        </p>
                    </div>
                ),
                cell: info => {
                    const lastSentAt = info.getValue();

                    if (!lastSentAt) {
                        return <EmptyData />;
                    }

                    return isoDateFromServer(lastSentAt).toLocaleString(DateTime.DATETIME_MED);
                },
            }),
            columnHelper.accessor('next_run_at', {
                header: () => (
                    <div className={'flex gap-1'}>
                        <p>Next scheduled send</p>
                        <p className={'text-xs leading-5 text-gray-400'}>
                            ({DateTime.now().offsetNameShort})
                        </p>
                    </div>
                ),
                cell: info => {
                    const nextRunAt = info.getValue();

                    if (!nextRunAt) {
                        return <EmptyData />;
                    }

                    return isoDateFromServer(nextRunAt).toLocaleString(DateTime.DATETIME_MED);
                },
            }),
            columnHelper.accessor('frequency', {
                header: 'Frequency',
                cell: cell => <p>{frequencyToReadable(cell.getValue())}</p>,
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: info => capitalize(info.cell.getValue()),
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const reportSchedule = info.row.original;
                    const isRecurring = reportSchedule.frequency !== 'one_time';
                    const isActive = reportSchedule.status === 'published';
                    return (
                        <DropdownMenu.Root key={reportSchedule.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    onSelect={e => e.preventDefault()}
                                    sideOffset={5}
                                    align={'end'}
                                >
                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/reports/${report.id}/schedules/${reportSchedule.id}`}
                                    >
                                        Update schedule
                                    </DropdownMenu.LinkItem>
                                    {isRecurring && isActive && (
                                        <PauseReportScheduleDialog.Root
                                            scheduleId={reportSchedule.id}
                                        >
                                            <PauseReportScheduleDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Pause sharing
                                                </DropdownMenu.Item>
                                            </PauseReportScheduleDialog.Trigger>
                                        </PauseReportScheduleDialog.Root>
                                    )}
                                    {isRecurring && !isActive && (
                                        <ResumeReportScheduleDialog.Root
                                            scheduleId={reportSchedule.id}
                                        >
                                            <ResumeReportScheduleDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Resume sharing
                                                </DropdownMenu.Item>
                                            </ResumeReportScheduleDialog.Trigger>
                                        </ResumeReportScheduleDialog.Root>
                                    )}
                                    {isRecurring && (
                                        <DeleteRecurringEmailDialog.Root
                                            scheduleId={reportSchedule.id}
                                        >
                                            <DeleteRecurringEmailDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Delete
                                                </DropdownMenu.Item>
                                            </DeleteRecurringEmailDialog.Trigger>
                                        </DeleteRecurringEmailDialog.Root>
                                    )}
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId, report.id],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        state: {
            pagination,
        },
        pageCount: data?.meta.last_page ?? -1,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        manualPagination: true,
        enableColumnPinning: false,
        enableHiding: false,
        enableSorting: false,
        meta: {
            enableColumnOrder: false,
            to: cell =>
                `/accounts/${accountId}/reports/${report.id}/schedules/${cell.row.original.id}`,
        },
    });

    useSyncPaginatedData(table, data);

    return (
        <Content>
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-4'}>
                    <Breadcrumbs.Root>
                        <Breadcrumbs.Crumb to={`/accounts/${accountId}/reports`}>
                            Reports
                        </Breadcrumbs.Crumb>
                        <Breadcrumbs.Crumb to={`/accounts/${accountId}/reports/${report.id}`}>
                            {report.name}
                        </Breadcrumbs.Crumb>
                        <Breadcrumbs.Crumb
                            to={`/accounts/${accountId}/reports/${report.id}/schedules`}
                        >
                            Report schedules
                        </Breadcrumbs.Crumb>
                    </Breadcrumbs.Root>
                    <p className={'text-2xl font-medium leading-none'}>
                        Shared reports for {report.name}
                    </p>
                </div>
                <Table.Root isLoading={isPending} table={table} variant="bordered">
                    <Box className="flex flex-col">
                        <Table.Content id={'report-schedules-index'}>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'} className="max-w-full gap-6">
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faEnvelope} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No recurring emails</Table.EmptyTitle>
                        </Table.Empty>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown />
                        </Table.Pagination>
                    </Box>
                </Table.Root>
            </div>
        </Content>
    );
};

const frequencyToReadable = (frequency: ReportScheduleRecurringFrequency) => {
    switch (frequency) {
        case 'one_time':
            return 'One-time';
        case 'daily':
        case 'weekly':
        case 'monthly':
            return capitalize(frequency);
    }
};

export { ReportSchedulesIndex };
